import {
  Box,
  Container,
  SimpleGrid,
  VStack,
  HStack,
  Heading,
  Text,
  Button,
  Icon,
  useColorModeValue,
  Spinner,
} from "@chakra-ui/react";
import { FiArrowLeft, FiArrowDown } from "react-icons/fi";
import { useNavigate, useParams } from "react-router-dom";
import { Navbar } from "../components/landing/Navbar";
import { Footer } from "../components/landing/Footer";
import { PageHero } from "../components/common/PageHero";
import { SectionWrapper } from "../components/common/SectionWrapper";
import { FundCard } from "../components/funds/FundCard";
import { useFunds } from "../hooks/useFunds";
import { useFundInvestNavigation } from "../hooks/useFundInvestNavigation";
import {
  FUND_CATEGORY_CONFIG_BY_KEY,
  filterFundsByCategory,
  isVisibleInFundCards,
  sortFundsByPriority,
  type FundCategoryKey,
} from "../config/fundCategories";

export const FundsCategoryPage = () => {
  const { category } = useParams<{ category: string }>();
  const navigate = useNavigate();
  const { data, isLoading } = useFunds();
  const navigateToInvest = useFundInvestNavigation();

  const config = FUND_CATEGORY_CONFIG_BY_KEY[category as FundCategoryKey];
  const textColor = useColorModeValue("gray.800", "white");
  const subtitleColor = useColorModeValue("gray.500", "gray.400");

  if (!config) {
    return (
      <Box dir="rtl" overflowX="hidden">
        <Navbar />
        <Box py={40} textAlign="center">
          <Container maxW="container.md">
            <VStack spacing={6}>
              <Heading color={textColor} fontSize="2xl">
                دسته‌بندی یافت نشد
              </Heading>
              <Button
                colorScheme="brand"
                onClick={() => navigate("/funds")}
                rightIcon={
                  <Icon verticalAlign="middle" as={FiArrowLeft as any} />
                }
              >
                بازگشت به صندوق‌ها
              </Button>
            </VStack>
          </Container>
        </Box>
        <Footer />
      </Box>
    );
  }

  const filteredFunds = sortFundsByPriority(
    filterFundsByCategory(data?.items || [], config?.key).filter((fund) =>
      isVisibleInFundCards(fund.fundRegisterCode),
    ),
  );

  const scrollToGrid = () => {
    const el = document.getElementById("funds-grid");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <Box dir="rtl" overflowX="hidden">
      <Navbar />
      <PageHero
        title={config.heroTitle || config.title}
        titleHighlight={config.heroHighlight || "تأمین سرمایه امید"}
        subtitle={config.heroSubtitle || config.description || ""}
        primaryAction={{
          label: "مشاهده صندوق‌ها",
          icon: FiArrowDown,
          onClick: scrollToGrid,
        }}
        secondaryAction={{
          label: "همه دسته‌بندی‌ها",
          onClick: () => navigate("/funds"),
        }}
      />

      {/* Intro */}
      <SectionWrapper contentMaxW="container.xl">
        <VStack spacing={4} textAlign="center" maxW="800px" mx="auto">
          <Heading
            as="h2"
            fontSize={{ base: "xl", md: "2xl" }}
            color={textColor}
          >
            {config.introTitle || config.title}
          </Heading>
          <Text
            fontSize={{ base: "sm", md: "md" }}
            color={subtitleColor}
            lineHeight="tall"
          >
            {config.introText || config.description || ""}
          </Text>
        </VStack>
      </SectionWrapper>

      {/* Funds Grid */}
      <SectionWrapper alt id="funds-grid" contentMaxW="container.xl">
        <VStack spacing={4} textAlign="center" mb={10}>
          <Heading
            as="h2"
            fontSize={{ base: "xl", md: "2xl", lg: "3xl" }}
            color={textColor}
          >
            {config.title}
          </Heading>
          <Text fontSize={{ base: "sm", md: "md" }} color={subtitleColor}>
            صندوق مورد نظر خود را انتخاب کنید
          </Text>
        </VStack>

        {isLoading ? (
          <HStack justify="center" py={10}>
            <Spinner color="brand.500" />
            <Text color={subtitleColor}>در حال دریافت اطلاعات صندوق‌ها...</Text>
          </HStack>
        ) : (
          <SimpleGrid
            columns={{ base: 1, md: 2, lg: filteredFunds.length >= 3 ? 3 : 2 }}
            spacing={{ base: 6, md: 8 }}
            w="100%"
            alignItems="stretch"
          >
            {filteredFunds.map((fund, index) => (
              <FundCard
                key={fund.fundRegisterCode}
                fund={fund}
                index={index}
                primaryAction={{
                  label: "سرمایه‌گذاری",
                  icon: FiArrowLeft,
                  onClick: () => navigateToInvest(fund.fundRegisterCode),
                }}
              />
            ))}
          </SimpleGrid>
        )}
      </SectionWrapper>

      <Footer />
    </Box>
  );
};
